import {
    GRANITE_SURFACE_FINISH,
    applyGraniteSurfaceAppearance,
    ensureGranitePhysicalMaterial,
} from '../../../utils/three/graniteSurfaceMaterial';

export const SURFACE_FINISH = GRANITE_SURFACE_FINISH;
export { ensureGranitePhysicalMaterial };

export const SURFACE_POLICY_MODE = Object.freeze({
    MODEL: 'model',
    ALL_POLISHED: 'allPolished',
});

const compactSpaces = (value = '') => String(value || '')
    .replace(/[._-]+/g, ' ')
    .replace(/[()\[\]{}]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const stripBlenderSuffix = (value = '') => compactSpaces(value)
    .replace(/(?:\s|^)(?:copy|копия)\s*\d*$/iu, '')
    .replace(/\s+\d{1,4}$/u, '')
    .trim();

export const normalizeGltfMaterialName = (value = '') => stripBlenderSuffix(
    String(value || '')
        .trim()
        .toLowerCase()
        .replace(/ё/g, 'е')
);

export const getOriginalGltfMaterialName = (material, fallbackIndex = 0) => (
    String(material?.name || '').trim() || `Материал GLB ${fallbackIndex + 1}`
);

export const detectSurfaceFinishRole = (materialOrName, fallbackIndex = 0) => {
    const material = materialOrName && typeof materialOrName === 'object'
        ? materialOrName
        : null;
    const originalName = material
        ? getOriginalGltfMaterialName(material, fallbackIndex)
        : String(materialOrName || '');
    const normalizedName = normalizeGltfMaterialName(originalName);

    const hasUnpolishedMarker = (
        /(?:^|\s)не\s*полир/u.test(normalizedName)
        || /(?:^|\s)неполир/u.test(normalizedName)
        || /(?:^|\s)матов/u.test(normalizedName)
        || /(?:^|\s)шлифован/u.test(normalizedName)
        || /(?:^|\s)unpolish/u.test(normalizedName)
        || /(?:^|\s)unpolished/u.test(normalizedName)
        || /(?:^|\s)matte/u.test(normalizedName)
        || /(?:^|\s)rough/u.test(normalizedName)
        || /(?:^|\s)raw\s*(?:stone|granite)?/u.test(normalizedName)
    );

    if (hasUnpolishedMarker) return SURFACE_FINISH.UNPOLISHED;

    const hasPolishedMarker = (
        /(?:^|\s)полир/u.test(normalizedName)
        || /(?:^|\s)глян/u.test(normalizedName)
        || /(?:^|\s)polish/u.test(normalizedName)
        || /(?:^|\s)polished/u.test(normalizedName)
        || /(?:^|\s)gloss/u.test(normalizedName)
    );

    if (hasPolishedMarker) return SURFACE_FINISH.POLISHED;

    const roughness = Number(material?.roughness);

    if (Number.isFinite(roughness)) {
        if (roughness >= 0.62) return SURFACE_FINISH.UNPOLISHED;
        if (roughness <= 0.35) return SURFACE_FINISH.POLISHED;
    }

    return SURFACE_FINISH.UNKNOWN;
};

export const getSurfaceFinishLabel = (finish) => {
    if (finish === SURFACE_FINISH.UNPOLISHED) return 'Неполированная поверхность';
    if (finish === SURFACE_FINISH.POLISHED) return 'Полированная поверхность';

    return 'Обработка не определена';
};

export const getSurfaceFinishShortLabel = (finish) => {
    if (finish === SURFACE_FINISH.UNPOLISHED) return 'Неполированная';
    if (finish === SURFACE_FINISH.POLISHED) return 'Полированная';

    return 'По материалу GLB';
};

const normalizeSlotOverride = (value) => {
    const materialIndex = Math.max(0, Math.floor(Number(value?.materialIndex) || 0));
    const finish = [SURFACE_FINISH.UNPOLISHED, SURFACE_FINISH.POLISHED]
        .includes(value?.finish)
        ? value.finish
        : SURFACE_FINISH.AUTO;

    if (finish === SURFACE_FINISH.AUTO) return null;

    return { materialIndex, finish };
};

export const normalizeSurfaceFinishPolicy = (value = {}) => {
    const allowPolishOverride = value?.allowPolishOverride === true;
    const requestedMode = value?.mode === SURFACE_POLICY_MODE.ALL_POLISHED
        ? SURFACE_POLICY_MODE.ALL_POLISHED
        : SURFACE_POLICY_MODE.MODEL;
    const slotOverrides = [];
    const seen = new Set();

    (Array.isArray(value?.slotOverrides) ? value.slotOverrides : []).forEach((item) => {
        const normalized = normalizeSlotOverride(item);
        if (!normalized || seen.has(normalized.materialIndex)) return;

        seen.add(normalized.materialIndex);
        slotOverrides.push(normalized);
    });

    return {
        autoDetect: value?.autoDetect !== false,
        allowPolishOverride,
        mode: allowPolishOverride ? requestedMode : SURFACE_POLICY_MODE.MODEL,
        slotOverrides: allowPolishOverride ? slotOverrides : [],
    };
};

export const getSlotSurfaceOverride = (policy, materialIndex) => {
    const normalized = normalizeSurfaceFinishPolicy(policy);
    const match = normalized.slotOverrides.find((item) => (
        item.materialIndex === Math.max(0, Math.floor(Number(materialIndex) || 0))
    ));

    return match?.finish || SURFACE_FINISH.AUTO;
};

export const getEffectiveSurfaceFinish = ({
    material,
    materialIndex = 0,
    policy = {},
}) => {
    const normalized = normalizeSurfaceFinishPolicy(policy);
    const detected = detectSurfaceFinishRole(material, materialIndex);

    if (!normalized.allowPolishOverride) return detected;

    const explicitOverride = getSlotSurfaceOverride(normalized, materialIndex);
    if (explicitOverride !== SURFACE_FINISH.AUTO) return explicitOverride;

    if (normalized.mode === SURFACE_POLICY_MODE.ALL_POLISHED) {
        return SURFACE_FINISH.POLISHED;
    }

    return detected;
};

export const updateSurfaceFinishSlotOverride = (policy, materialIndex, finish) => {
    const normalized = normalizeSurfaceFinishPolicy(policy);
    const index = Math.max(0, Math.floor(Number(materialIndex) || 0));
    const nextFinish = [SURFACE_FINISH.UNPOLISHED, SURFACE_FINISH.POLISHED]
        .includes(finish)
        ? finish
        : SURFACE_FINISH.AUTO;
    const nextOverrides = normalized.slotOverrides.filter((item) => item.materialIndex !== index);

    if (nextFinish !== SURFACE_FINISH.AUTO) {
        nextOverrides.push({ materialIndex: index, finish: nextFinish });
    }

    return normalizeSurfaceFinishPolicy({
        ...normalized,
        mode: SURFACE_POLICY_MODE.MODEL,
        slotOverrides: nextOverrides,
    });
};

const getCatalogSurfaceHaystack = (catalog = {}) => normalizeGltfMaterialName([
    catalog?.key,
    catalog?.name,
    ...(Array.isArray(catalog?.aliases) ? catalog.aliases : []),
].filter(Boolean).join(' '));

export const isUnpolishedSurfaceCatalog = (catalog = {}) => {
    const haystack = getCatalogSurfaceHaystack(catalog);

    return (
        /(?:^|\s)не\s*полир/u.test(haystack)
        || /(?:^|\s)неполир/u.test(haystack)
        || /(?:^|\s)матовая?\s+поверхность/u.test(haystack)
        || /(?:^|\s)unpolished(?:\s+surface)?/u.test(haystack)
        || /(?:^|\s)rough(?:\s+surface)?/u.test(haystack)
    );
};

export const getUnpolishedSurfaceCatalog = (catalogs = []) => (
    (Array.isArray(catalogs) ? catalogs : []).find((catalog) => (
        catalog?.enabled !== false && isUnpolishedSurfaceCatalog(catalog)
    )) || null
);

export const isSurfacePseudoCatalog = (catalog = {}) => {
    const haystack = getCatalogSurfaceHaystack(catalog);

    return (
        isUnpolishedSurfaceCatalog(catalog)
        || /(?:^|\s)полированная\s+поверхность/u.test(haystack)
        || /(?:^|\s)polished(?:\s+surface)?/u.test(haystack)
        || /(?:^|\s)surface\s*finish/u.test(haystack)
    );
};

export const applySurfaceFinishToThreeMaterial = (
    material,
    finish,
    fallbackPreset = {}
) => applyGraniteSurfaceAppearance(material, finish, fallbackPreset);
